import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Send, Heart, Pill } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fhirDataStore } from '@/utils/fhirDataUtils';
import { generateDisputePDF } from '@/utils/pdfGenerator';

interface RecordItem {
  id: string;
  type: 'condition' | 'medication' | 'lab' | 'procedure';
  title: string;
  description: string;
  date: string;
  provider: string;
  flagged: boolean;
  reason?: string;
  severity: 'high' | 'medium' | 'low';
}
interface DisputePacketGeneratorProps {
  selectedPatientId?: string;
  flaggedItems?: RecordItem[];
}
export const DisputePacketGenerator = ({
  selectedPatientId = 'me',
  flaggedItems = []
}: DisputePacketGeneratorProps) => {
  const {
    toast
  } = useToast();
  const [disputeForm, setDisputeForm] = useState({
    providerName: '',
    providerFax: '',
    requestedChange: '',
    justification: '',
    patientName: '',
    patientDOB: '',
    patientAddress: ''
  });
  const [generatedPacket, setGeneratedPacket] = useState<string | null>(null);
  const [disputeId, setDisputeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const loadPatientInfo = useCallback(async () => {
    setLoading(true);
    try {
      let patientData = null;
      if (selectedPatientId === 'me') {
        // Load from localStorage for 'me'
        const storedData = localStorage.getItem('fhir_data');
        if (storedData) {
          patientData = JSON.parse(storedData);
        }
      } else {
        // Load from IndexedDB for other patients
        patientData = await fhirDataStore.getFHIRData(selectedPatientId);
      }

      if (patientData && patientData.entry) {
        const patientResource = patientData.entry.find((entry: any) => entry.resource?.resourceType === 'Patient')?.resource;
        if (patientResource) {
          const name = patientResource.name?.[0];
          const fullName = name ? `${name.given?.join(' ') || ''} ${name.family || ''}`.trim() : '';

          // Get address
          const address = patientResource.address?.[0];
          const fullAddress = address ? `${address.line?.join(' ') || ''}, ${address.city || ''}, ${address.state || ''} ${address.postalCode || ''}`.trim() : '';
          setDisputeForm(prev => ({
            ...prev,
            patientName: fullName,
            patientDOB: patientResource.birthDate || '',
            patientAddress: fullAddress
          }));
        }
      }
    } catch (error) {
      console.error('Error loading patient info:', error);
    }
    setLoading(false);
  }, [selectedPatientId]);

  useEffect(() => {
    loadPatientInfo();
  }, [loadPatientInfo]);
  const handleInputChange = (field: string, value: string) => {
    setDisputeForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const generateDisputeId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `HSC-${new Date().getFullYear()}-${timestamp}${random}`.toUpperCase();
  };
  const getRecordIcon = (type: string) => {
    switch (type) {
      case 'condition':
        return <Heart className="h-4 w-4" />;
      case 'medication':
        return <Pill className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };
  const generateRecordsSummary = () => {
    if (flaggedItems.length === 0) return '';
    return flaggedItems.map((item, index) => `
${index + 1}. ${item.type.toUpperCase()}: ${item.title}
   Description: ${item.description}
   Date: ${new Date(item.date).toLocaleDateString()}
   Provider: ${item.provider}
   Issue: ${item.reason || 'Accuracy review requested'}
`).join('\n');
  };
  const generateDisputePacket = () => {
    if (flaggedItems.length === 0) {
      toast({
        title: "No records selected",
        description: "Please select records from the Record Review tab first.",
        variant: "destructive"
      });
      return;
    }
    if (!disputeForm.providerName || !disputeForm.requestedChange) {
      toast({
        title: "Missing required fields",
        description: "Please fill in provider name and requested changes.",
        variant: "destructive"
      });
      return;
    }
    const newDisputeId = generateDisputeId();
    setDisputeId(newDisputeId);
    const recordsSummary = generateRecordsSummary();
    const packet = `
HIPAA REQUEST FOR AMENDMENT TO PROTECTED HEALTH INFORMATION
45 CFR § 164.526

Dispute ID: ${newDisputeId}
Date: ${new Date().toLocaleDateString()}

TO: ${disputeForm.providerName}
FAX: ${disputeForm.providerFax}

RE: Request for Amendment to Protected Health Information

Dear Healthcare Provider,

This letter serves as a formal request for amendment to protected health information pursuant to 45 CFR § 164.526 of the HIPAA Privacy Rule.

PATIENT INFORMATION:
Name: ${disputeForm.patientName}
Date of Birth: ${disputeForm.patientDOB}
Address: ${disputeForm.patientAddress}

RECORDS TO BE AMENDED:
${recordsSummary}

REQUESTED CHANGES:
${disputeForm.requestedChange}

JUSTIFICATION:
${disputeForm.justification}

LEGAL BASIS:
This request is made pursuant to 45 CFR § 164.526, which grants individuals the right to request amendment of protected health information maintained in a designated record set when the individual believes the information is inaccurate or incomplete.

RESPONSE REQUIRED:
Please respond to this request within 60 days as required by HIPAA regulations. You may:
1. Accept the amendment and make the requested changes
2. Deny the request and provide written basis for denial
3. Request additional information if needed

TRACKING:
For your convenience, this dispute can be tracked online at:
https://qr.md/d/${newDisputeId}

Please acknowledge receipt of this request and provide your response using the tracking system or by contacting us directly.

Respectfully,
${disputeForm.patientName}

---
This request is generated by healthscores.ai
Compliance with HIPAA Privacy Rule 45 CFR § 164.526
    `;
    setGeneratedPacket(packet);
    toast({
      title: "Dispute packet generated",
      description: `Dispute ID ${newDisputeId} has been created with all required HIPAA documentation.`
    });
  };
  const handleSendDispute = async () => {
    if (!generatedPacket || !disputeId) return;

    try {
      // Prepare dispute data for API
      const disputeData = {
        disputeId,
        patientName: disputeForm.patientName,
        patientDOB: disputeForm.patientDOB,
        patientAddress: disputeForm.patientAddress,
        providerName: disputeForm.providerName,
        providerFax: disputeForm.providerFax,
        requestedChange: disputeForm.requestedChange,
        justification: disputeForm.justification,
        flaggedItems: flaggedItems,
        selectedPatientId
      };

      // Generate PDF
      toast({
        title: "Generating documents...",
        description: "Creating PDF and QR code for your dispute packet."
      });

      const pdfBlob = await generateDisputePDF(disputeData);
      
      // Convert PDF to base64 for API transmission
      const pdfBase64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          resolve(base64.split(',')[1]); // Remove data:application/pdf;base64, prefix
        };
        reader.readAsDataURL(pdfBlob);
      });

      // Send to API
      const token = localStorage.getItem('bearer_token');
      const response = await fetch('/api/send_dispute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          ...disputeData,
          pdfDocument: pdfBase64
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('Dispute sent successfully:', result);

      toast({
        title: "Dispute submitted successfully",
        description: `Dispute ${disputeId} has been sent to the provider. You can track its progress in the Active Disputes tab.`
      });

      // Reset form
      setDisputeForm(prev => ({
        ...prev,
        providerName: '',
        providerFax: '',
        requestedChange: '',
        justification: ''
      }));
      setGeneratedPacket(null);
      setDisputeId(null);
      
    } catch (error) {
      console.error('Error sending dispute:', error);
      toast({
        title: "Error sending dispute",
        description: "Failed to submit dispute. Please try again.",
        variant: "destructive"
      });
    }
  };
  return <div className="space-y-6">
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-2">Generate HIPAA Dispute Packet</h3>
        <p className="text-sm text-muted-foreground">
          Create a legally compliant HIPAA Request for Amendment with selected records from your review.
        </p>
      </Card>

      {/* Selected Records Summary */}
      {flaggedItems.length > 0 && <Card className="p-4">
          <h4 className="font-medium mb-3">Selected Records for Dispute ({flaggedItems.length})</h4>
          <div className="space-y-3">
            {flaggedItems.map(item => <div key={item.id} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
                {getRecordIcon(item.type)}
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h5 className="font-medium">{item.title}</h5>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {item.type}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                  {item.reason && <p className="text-xs text-orange-700 mt-1">
                      <strong>Issue:</strong> {item.reason}
                    </p>}
                </div>
              </div>)}
          </div>
        </Card>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <Card className="p-6">
          <h4 className="font-medium mb-4">Dispute Information</h4>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="providerName">Provider Name *</Label>
                <Input id="providerName" placeholder="Dr. Smith - Primary Care" value={disputeForm.providerName} onChange={e => handleInputChange('providerName', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="providerFax">Provider Fax</Label>
                <Input id="providerFax" placeholder="555-123-4567" value={disputeForm.providerFax} onChange={e => handleInputChange('providerFax', e.target.value)} />
              </div>
            </div>

            <div>
              <Label htmlFor="requestedChange">Requested Changes *</Label>
              <Textarea id="requestedChange" placeholder="Clearly describe what changes you are requesting for the selected records" value={disputeForm.requestedChange} onChange={e => handleInputChange('requestedChange', e.target.value)} />
            </div>

            <div>
              <Label htmlFor="justification">Justification *</Label>
              <Textarea id="justification" placeholder="Provide evidence or reasoning for why these changes are necessary" value={disputeForm.justification} onChange={e => handleInputChange('justification', e.target.value)} />
            </div>

            <h4 className="font-medium mt-6 mb-4">Patient Information</h4>
            
            {loading ? <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm text-muted-foreground">Loading patient information...</p>
              </div> : <>
                <div>
                  <Label htmlFor="patientName">Patient Name</Label>
                  <Input 
                    id="patientName" 
                    value={disputeForm.patientName} 
                    readOnly 
                    className="bg-gray-50 text-gray-600 select-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0" 
                    style={{ userSelect: 'none' }}
                    tabIndex={-1}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="patientDOB">Date of Birth</Label>
                    <Input 
                      id="patientDOB" 
                      type="date" 
                      value={disputeForm.patientDOB} 
                      readOnly 
                      className="bg-gray-50 text-gray-600 select-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0" 
                      style={{ userSelect: 'none' }}
                      tabIndex={-1}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="patientAddress">Address</Label>
                  <Textarea 
                    id="patientAddress" 
                    value={disputeForm.patientAddress} 
                    readOnly 
                    className="bg-gray-50 text-gray-600 select-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0" 
                    style={{ userSelect: 'none' }}
                    tabIndex={-1}
                  />
                </div>
              </>}

            <Button onClick={generateDisputePacket} className="w-full">
              <FileText className="h-4 w-4 mr-2" />
              Generate Dispute Packet
            </Button>
          </div>
        </Card>

        {/* Preview */}
        <Card className="p-6">
          <h4 className="font-medium mb-4">Generated Document Preview</h4>
          {generatedPacket ? <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded text-xs font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                {generatedPacket}
              </div>
              
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="w-full">
                    <Send className="h-4 w-4 mr-2" />
                    Send Dispute
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Confirm Dispute Submission</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      This will officially submit your dispute packet to the provider via secure transmission and 
                      create a tracking record with ID: <strong>{disputeId}</strong>
                    </p>
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline">Cancel</Button>
                      <Button onClick={handleSendDispute}>Confirm Send</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div> : <div className="text-center text-muted-foreground py-8">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              {flaggedItems.length > 0 ? <p>Complete the form to generate your HIPAA-compliant dispute packet for {flaggedItems.length} selected records</p> : <p>Select records from the Record Review tab first, then complete the form to generate your dispute packet</p>}
            </div>}
        </Card>
      </div>
    </div>;
};
